import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Skill } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  useGrantSkillPermission,
  useRevokeSkillPermission,
  useSkillPermissions,
} from "@/features/skills/hooks/useSkills";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  skill: Skill;
}

export function SkillPermissionsTab({ skill }: Props) {
  const { t } = useTranslation("skills");
  const { t: tc } = useTranslation("common");
  const { data: perms, isLoading } = useSkillPermissions(skill.id);
  const { data: teams } = useTeams({ include_inactive: false });
  const grant = useGrantSkillPermission(skill.id);
  const revoke = useRevokeSkillPermission(skill.id);
  const canManage = useCan(Action.SKILL_PERMISSION_GRANTED, {
    owner_team_id: skill.owner_team_id,
  });

  const [pickedTeam, setPickedTeam] = useState("");

  const grantedIds = new Set([
    skill.owner_team_id,
    ...(perms?.map((p) => p.team_id) ?? []),
  ]);
  const available = teams?.filter((t) => !grantedIds.has(t.id)) ?? [];
  const ownerTeam = teams?.find((t) => t.id === skill.owner_team_id);

  async function handleGrant() {
    if (!pickedTeam) return;
    try {
      await grant.mutateAsync({ team_id: pickedTeam, permission: "use" });
      toast.success(t("permissions.granted"));
      setPickedTeam("");
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleRevoke(teamId: string) {
    try {
      await revoke.mutateAsync(teamId);
      toast.success(t("permissions.revoked"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="space-y-4 py-4">
      <div className="rounded-md border p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-medium">
            {ownerTeam?.name ?? t("permissions.ownerFallback")}
          </span>
          <Badge variant="outline">{t("permissions.ownerBadge")}</Badge>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{tc("states.loading")}</p>
      ) : null}

      {perms && perms.length > 0 ? (
        <ul className="divide-y rounded-md border">
          {perms.map((p) => (
            <li
              key={p.team_id}
              className="flex items-center justify-between px-3 py-2"
            >
              <span className="text-sm font-medium">{p.team_name}</span>
              {canManage ? (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRevoke(p.team_id)}
                  disabled={revoke.isPending}
                  aria-label={t("permissions.revoke")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : !isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t("permissions.empty")}
        </p>
      ) : null}

      {canManage ? (
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold">{t("permissions.grantTitle")}</h3>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="skill-pick-team">{t("permissions.team")}</Label>
              <select
                id="skill-pick-team"
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={pickedTeam}
                onChange={(e) => setPickedTeam(e.target.value)}
              >
                <option value="">{t("form.labels.select")}</option>
                {available.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleGrant}
              disabled={!pickedTeam || grant.isPending}
            >
              {t("permissions.grant")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
